import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { Button, Dropdown, FeatureRadio } from '@praetorian-chariot/ui';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { Input, InputEvent } from '@/components/form/Input';
import { useModifyAccount } from '@/hooks/useAccounts';
import useInitialize, {
  CloudInitializeRequest,
  CloudInitializeResponse,
} from '@/hooks/useInitialize';
import { cn } from '@/utils/classname';
import { triggerConfetti } from '@/utils/confetti.util';
import { downloadIACTemplate } from '@/utils/download.util';
import { useStorage } from '@/utils/storage/useStorage.util';

const DEV_TYPE_OPTIONS = [
  { label: 'Cloudformation (Recommended)', value: 'cloudformation' },
  { label: 'Terraform', value: 'terraform' },
  { label: 'Manual', value: 'manual' },
];

type SelectionType = 'organization' | 'individual';

type FormState = {
  organization: { org_account_id: string; deployment_type: string };
  individual: { account_id: string; deployment_type: string };
};

export function AWS({ onClose }: { onClose: () => void }) {
  const [savedState, setSavedState] = useStorage<
    | {
        request: CloudInitializeRequest;
        response: CloudInitializeResponse;
      }
    | undefined
  >({ key: 'aws-integration-state' });

  const [selection, setSelection] = useState<SelectionType | undefined>(
    savedState?.request.org_account_id
      ? 'organization'
      : savedState?.request.account_id
        ? 'individual'
        : 'organization'
  );
  const [form, setForm] = useState<FormState>({
    organization: {
      org_account_id: savedState?.request.org_account_id || '',
      deployment_type: savedState?.request.deployment_type || '',
    },
    individual: {
      account_id: savedState?.request.account_id || '',
      deployment_type: savedState?.request.deployment_type || '',
    },
  });
  const [step, setStep] = useState(savedState ? 1 : 0);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const { mutateAsync: initializeCloud, status: initializeCloudStatus } =
    useInitialize();
  const { mutateAsync: link, status: linkStatus } = useModifyAccount('link');

  // Trigger confetti effect when entering step 2
  useEffect(() => {
    if (step === 2) {
      triggerConfetti({ delay: 300 });
    }
  }, [step]);

  // Handle input changes for accountId
  const handleInputChange =
    (type: SelectionType, field: string) => (e: InputEvent) => {
      setForm(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          [field]: e.target.value,
        },
      }));
    };

  // Handle Dropdown selection for deployment_type
  const handleDropdownSelect =
    (type: SelectionType) => (option: { value?: string | number }) => {
      if (typeof option.value === 'string') {
        setForm(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            deployment_type: option.value,
          },
        }));
      }
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selection) return;

    const request = {
      provider: 'aws',
      ...form[selection],
    };

    const response = await initializeCloud(request);
    setSavedState({
      request,
      response,
    });
    setStep(1);
  };

  // Find the selected deployment_type option for Dropdown
  const getSelectedDropdownOption = (type: SelectionType) => {
    const val = form[type].deployment_type;
    return val ? [DEV_TYPE_OPTIONS.find(opt => opt.value === val)!] : [];
  };

  const handleCancel = () => {
    setSavedState(undefined);
    setStep(0);
    setSelection(undefined);
    setHasDownloaded(false);
    setForm({
      organization: { org_account_id: '', deployment_type: '' },
      individual: { account_id: '', deployment_type: '' },
    });
    onClose();
  };

  const handleFinish = async () => {
    await link({
      username: 'amazon',
      config:
        selection === 'organization'
          ? {
              org_account_id: form.organization.org_account_id,
              external_id: savedState?.response?.username,
            }
          : {
              account_id: form.individual.account_id,
              external_id: savedState?.response?.username,
            },
      value:
        selection === 'organization'
          ? form.organization.org_account_id
          : form.individual.account_id,
    });

    setSavedState(undefined);

    setStep(2);
  };

  return (
    <div className="">
      <div className="bg-chariot-stroke mb-6 h-[2px] w-full shrink-0 rounded-lg" />
      {step === 0 && (
        <>
          <div className="mb-4 font-bold">Scope of Integration</div>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 [&>button]:pr-4 [&>button>.text-left]:w-full"
          >
            <FeatureRadio
              id="aws-radio-organization"
              title="Organization"
              description="Integrates all accounts within the organization"
              checked={selection === 'organization'}
              onChange={() => setSelection('organization')}
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-2">
                  <label className="text-sm font-bold">Account ID</label>
                  <Input
                    className="bg-transparent"
                    name="accountId"
                    value={form.organization.org_account_id}
                    onChange={handleInputChange(
                      'organization',
                      'org_account_id'
                    )}
                    placeholder="000000000000"
                    required
                  />
                  <label className="text-chariot-text-primary mb-1 text-sm font-semibold">
                    Deployment Type
                  </label>
                  <Dropdown
                    className="[&>button]:w-full [&>button]:justify-between"
                    options={DEV_TYPE_OPTIONS}
                    selected={getSelectedDropdownOption('organization')}
                    onSelect={handleDropdownSelect('organization')}
                    placeholder="Select deployment type"
                  />
                </div>
              }
            />
            <FeatureRadio
              id="aws-radio-individual"
              title="Individual Account"
              description="Integrates a single account"
              checked={selection === 'individual'}
              onChange={() => setSelection('individual')}
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-2">
                  <label className="text-sm font-bold">Account ID</label>
                  <Input
                    className="bg-transparent"
                    name="accountId"
                    value={form.individual.account_id}
                    onChange={handleInputChange('individual', 'account_id')}
                    placeholder="000000000000"
                    required
                  />
                  <label className="text-chariot-text-primary mb-1 text-sm font-semibold">
                    Deployment Type
                  </label>
                  <Dropdown
                    className="[&>button]:w-full [&>button]:justify-between"
                    options={DEV_TYPE_OPTIONS}
                    selected={getSelectedDropdownOption('individual')}
                    onSelect={handleDropdownSelect('individual')}
                    placeholder="Select deployment type"
                  />
                </div>
              }
            />
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                styleType="secondary"
                label="Cancel"
                className="px-6"
                onClick={handleCancel}
                size="xs"
              />
              <Button
                type="submit"
                label="Submit"
                disabled={
                  !selection ||
                  !(selection === 'organization'
                    ? form.organization.org_account_id
                    : form.individual.account_id) ||
                  !form[selection].deployment_type ||
                  initializeCloudStatus === 'pending'
                }
                onClick={() => {}}
                className="px-6"
                size="xs"
              />
            </div>
          </form>
        </>
      )}
      {step === 1 && (
        <div>
          {form[selection!].deployment_type !== 'manual' ? (
            <>
              <div className="mb-4 font-bold">Download IAC Template</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                To finish AWS Terraform setup, please download the IAC Template,
                then click finish. For more information, please reference our{' '}
                <a
                  href="https://docs.praetorian.com/articles/39508437793947-how-aws-integration-works-deep-dive"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  AWS documentation
                </a>
                .
              </div>

              <div className="mb-8 flex items-center gap-2">
                <div className="text-chariot-text-secondary flex items-center gap-2 text-sm">
                  <InformationCircleIcon className="text-chariot-stroke size-4" />
                  <span>Must download IAC Template before finishing</span>
                </div>
              </div>
              <Button
                size="xs"
                styleType="rounded"
                label="Download IAC Template"
                endIcon={
                  hasDownloaded ? (
                    <CheckCircleIcon className="text-chariot-stroke-info size-4" />
                  ) : (
                    <ChevronRightIcon className="size-4 stroke-[3px]" />
                  )
                }
                onClick={() => {
                  if (savedState?.response?.template) {
                    const deploymentType = form[selection!].deployment_type;
                    const point =
                      selection === 'organization'
                        ? form.organization.org_account_id
                        : form.individual.account_id;

                    downloadIACTemplate({
                      template: savedState.response.template,
                      filename: `chariot-aws-${point}`,
                      deploymentType: deploymentType,
                    });
                    setHasDownloaded(true);
                  }
                }}
                className={cn(
                  'mb-8',
                  hasDownloaded &&
                    'border-chariot-stroke-info text-chariot-stroke-info hover:bg-chariot-status-info/20 hover:text-chariot-stroke-info border-dashed'
                )}
              />
            </>
          ) : (
            <div>
              <div className="mb-4 font-bold">Manual Setup</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                For manual setup, please follow our{' '}
                <a
                  href="https://docs.praetorian.com/articles/39508437793947-how-aws-integration-works-deep-dive"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  AWS documentation
                </a>{' '}
                to configure your AWS environment.
              </div>
              {savedState?.response?.username && (
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-bold">
                    External ID
                  </label>
                  <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-3">
                    <code className="text-chariot-text-primary text-sm">
                      {savedState.response.username}
                    </code>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              styleType="secondary"
              label="Cancel"
              onClick={handleCancel}
              size="xs"
            />
            <Button
              className="px-4"
              type="button"
              styleType="primary"
              label={linkStatus === 'pending' ? 'Finishing...' : 'Finish'}
              disabled={
                linkStatus === 'pending' ||
                (form[selection!].deployment_type !== 'manual' &&
                  !hasDownloaded)
              }
              onClick={handleFinish}
              size="xs"
            />
          </div>
        </div>
      )}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center py-12 text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-chariot-text-primary mb-2 text-2xl font-bold"
          >
            Hooray! You&apos;re All Set!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-chariot-text-secondary mb-8 text-sm"
          >
            Your AWS setup is complete. Chariot will now scan and report on this
            environment
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Button
              styleType="primary"
              label="Done"
              onClick={() => {
                handleCancel();
              }}
              size="sm"
              className="px-8"
            />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
